import React from 'react';

import Footer from '../Components/Footer';

const TermsAndConditions = () => {
    const renderListItems = (items) => {
        return items.map((item, index) => {
            if (typeof item === 'string') {
                return <li key={index}>{item}</li>;
            }
            if (item.bold) {
                return <li key={index}><strong>{item.bold}</strong>{item.text}</li>;
            }
            if (item.text && item.sublist) {
                const SublistComponent = item.sublist.type === 'ol' ? 'ol' : 'ul';
                return (
                    <li key={index}>
                        {item.text}
                        <SublistComponent className="list-disc pl-6 mt-2 space-y-2">
                            {renderListItems(item.sublist.items)}
                        </SublistComponent>
                    </li>
                );
            }
            return null;
        });
    };

    const renderContent = (content) => {
        if (!content) return null;
        return content.map((item, index) => {
            if (typeof item === 'string') {
                return <p key={index} className="mb-4 text-neutral-300 leading-relaxed">{item}</p>;
            }
            if (item.title && item.content) { // For subsections passed in content
                return (
                    <div key={index} className="mt-6">
                        <h3 className="text-xl font-semibold mb-3 text-yellow-400">{item.title}</h3>
                        <div className="space-y-4 text-gray-300">
                            {renderContent(item.content)}
                        </div>
                    </div>
                );
            }
            if (item.type === 'ul' || item.type === 'ol') {
                const ListComponent = item.type;
                return (
                    <ListComponent key={index} className="list-disc pl-6 space-y-2 mb-4">
                        {renderListItems(item.items)}
                    </ListComponent>
                );
            }
            return null;
        });
    };

    const sections = [
        {
            title: "1. INTRODUCTION. ACCEPTANCE OF THE ToS",
            content: [
                "This page includes the applicable terms of service for users to access and use the Platform (the 'ToS').",
                "The access to the Platform implies the acceptance of these ToS.",
            ],
        },
        {
            title: "2. DEFINITIONS",
            content: [
                {
                    type: "ul",
                    items: [
                        { bold: "Platform: ", text: "The website hosted at the domain www.crezar.com." },
                        { bold: "User: ", text: "Any person who browses the Platform, regardless of whether or not they register." },
                        { bold: "Registered User: ", text: "Any User who has completed the registration process on the Platform." },
                        { bold: "Streamer: ", text: "A Registered User who creates and shares content on the Platform." },
                        { bold: "Viewer: ", text: "A Registered User who views content created by Streamers." },
                        { bold: "ToS: ", text: "These Terms of Service." },
                    ],
                },
            ],
        },
        {
            title: "3. THE PLATFORM",
            content: [
                "The Platform is a space that allows Streamers to share content with Viewers. Crezar acts as an intermediary, providing the necessary technology for this interaction to take place.",
                "Crezar does not create, edit, or control the content shared by Streamers. Therefore, Crezar is not responsible for the legality, quality, or accuracy of such content.",
            ],
        },
        {
            title: "4. REGISTRATION AND ACCESS",
            content: [
                "To access certain features of the Platform, you must register and create an account. You must provide accurate and complete information and keep your account information updated.",
                "You are responsible for maintaining the confidentiality of your account and password, and for all activities that occur under your account. You agree to notify Crezar immediately of any unauthorized use of your account.",
            ],
        },
        {
            title: "5. USER OBLIGATIONS",
            content: [
                "As a User of the Platform, you agree to:",
                {
                    type: "ul", items: [
                        "Use the Platform in a lawful manner and in accordance with these ToS.",
                        "Not to use the Platform for any illegal or unauthorized purpose.",
                        "Not to harass, abuse, or harm another person, including other Users.",
                        "Not to upload or share content that is defamatory, obscene, or otherwise objectionable.",
                    ]
                },
            ],
        },
        {
            title: "6. ACCOUNTS",
            subsections: [
                {
                    title: "6.1. Streamer Account",
                    content: [
                        "To set up your account as a Streamer you must access through the social networks enabled on the platform (i.e., Twitch, YouTube, Facebook, Google).",
                        "The registration ID and password must be kept in a safe place and must not be given to any third party. Once you have obtained your ID and password, you will be able to access the 'My Account' section, where you can modify your registration details and view and monitor your account status.",
                        "If you have forgotten your user ID or password, you will need to contact the Crezar support team.",
                        "Your account is personal and non-transferable, that is, it may not be sold, transferred, assigned, fully or partially leased to any third party.",
                        "You can delete your account at any time.",
                    ],
                },
                {
                    title: "6.2. Viewer Account",
                    content: [
                        "To set up your account as a Viewer you must access through the social networks enabled on the platform (i.e., Twitch, YouTube, Facebook, Google).",
                        "The registration ID and password must be kept in a safe place and must not be given to any third party. Once you have obtained your ID and password, you will be able to access the 'My Account' section, where you can modify your registration details and view and monitor your account status.",
                        "If you have forgotten your user ID or password, you will need to contact the Crezar support team.",
                        "Your account is personal and non-transferable, that is, it may not be sold, transferred, assigned, fully or partially leased to any third party.",
                        "You can delete your account at any time.",
                    ],
                },
            ]
        },
        {
            title: "7. STREAMER PRODUCTS",
            subsections: [
                {
                    title: "7.1. Chests and cards",
                    content: [
                        "The Streamer makes their card collection available to Viewers. The cards are sold through chests (loots) in packs of three, two or one. The cards included in chests are random and each of them contains a different interaction with the Viewer.",
                        "Fees:",
                        {
                            type: 'ul',
                            items: [
                                "Crezar charges a twenty percent (20%) fee on all of the Viewer Payments made to you.",
                                "In the event that you become a Partner as provided in the Partner Terms and Conditions, Crezar may reduce this fee by up to ten percent (10%).",
                                "This fee is deducted from the Viewer Payments."
                            ]
                        }
                    ]
                },
                {
                    title: "7.2. Auctions",
                    content: [
                        "Auctioned goods and auction conditions:",
                        {
                            type: 'ul',
                            items: [
                                "At an auction, the streamer may offer any item they want, including any kind of Content, provided that it complies with each and every Term of Service. The Streamer must include a detailed description of the item to be auctioned on the Platform.",
                                "The Streamer will set (i) the starting price for the auction of each item, which will constitute the minimum sale price, i.e., the minimum price at which it may be awarded to a User; (ii) the bidding scale; and (iii) the bidding deadline; all within the limits and functionalities allowed by the Platform.",
                                "Once an auction has started, the Streamer will not be able to modify the aforementioned conditions and will not be able to withdraw the auctioned items in any case. The Streamer is obliged to deliver the auctioned goods to the User that wins the auction under the established conditions."
                            ]
                        },
                        "Crezar may not accept or may cancel an auction if it considers that there is any inaccuracy or omission about the auctioned item or the conditions set by the Streamer, or that it does not comply with any of the Terms of Service.",
                        "The Streamer auctioning goods expressly authorizes Crezar to display, reproduce and publish through the Platform images of the auctioned goods. To this end, by posting an item on the auction portal, the Streamer grants Crezar a free, indefinite, irrevocable, sub-licensable, international license to use, reproduce, distribute, and publicly display the auctioned item.",
                        "Responsibility for auctioned goods:",
                        {
                            type: 'ul',
                            items: [
                                "They must be described in detail in the Platform.",
                                "They are exclusive property of the Streamer, who holds all rights necessary to auction and distribute them.",
                                "They are freely transferable.",
                                "They must not infringe any rights of third parties.",
                                "They must be free of charges, encumbrances and claims of any kind.",
                                "They must be of satisfactory quality considering their description and the information provided about them."
                            ]
                        },
                        "Deposit:",
                        {
                            type: 'ul',
                            items: [
                                "Any User wishing to participate in an auction by placing a bid must make a deposit of ten euros or US dollars (€/$10).",
                                "This amount will be withheld from the User's account until the end of the auction.",
                                "In the event that the User is the winner of the auction, the amount that the User will pay will be the equivalent of the awarded item minus the amount of the deposit, which will be considered as an amount paid on account of the price.",
                                "In the event that the User is not the winner of the auction, the amount withheld as a deposit will be released within seven (7) calendar days."
                            ]
                        },
                        "Bids:",
                        {
                            type: 'ul',
                            items: [
                                "Any User may place bids from the time the auction starts until the deadline, following the bidding scale established on the Platform for each auctioned item.",
                                "By placing a bid, the User is obliged to pay the amount of the winning bid to the Streamer that holds the auction and, consequently, to follow the procedure provided in these Auction ToS to complete the corresponding payment.",
                                "Each auctioned item will be awarded to the highest bid received before the deadline.",
                                "The Platform offers the possibility of placing automatic bids, consisting of the User setting a limit amount to be paid for the auctioned item. Once an automatic bid has been placed, the system will place the bid at the lowest possible price allowed by the other bids, until the maximum limit set by the User is reached. In the event that an automatic bid is successful, the auctioned item will be awarded to the User who placed the automatic bid.",
                                "It is prohibited for the Streamer or any third party to place bids with the intent to increase the auction value of the goods. In the event that Crezar detects any such action, without prejudice to any legal action Crezar may take, Crezar may immediately terminate Users' accounts and cancel or terminate the auction affected by such bidding, even if the auction has already ended."
                            ]
                        },
                        "Awarding of the auctioned goods:",
                        {
                            type: 'ul',
                            items: [
                                "The auctioned goods will be awarded to the bidder who has placed the highest bid at the time the auction ends. Crezar may make the result of each auction public.",
                                "Crezar will communicate to the winning User the result of the auction and the item that has been awarded. The winning User of the auction must confirm the payment of the difference between the amount of the winning bid and the deposit within twenty-four (24) hours following the award in the auction. Such confirmation of payment must be made through the system established for this purpose on the Platform."
                            ]
                        },
                        "Non-payment:",
                        {
                            type: 'ul',
                            items: [
                                {
                                    text: "If the winning User of an auction does not confirm the payment of the winning bid within the indicated twenty-four (24) hours following the award in the auction:",
                                    sublist: {
                                        type: 'ul',
                                        items: [
                                            "The auctioned property will be awarded to the next highest bidder.",
                                            "The User who has not completed the payment will forfeit the amount of the Deposit, which will be retained by Crezar as an auction administration fee and delivered to the Streamer, after deducting the relevant commission.",
                                            "Crezar may suspend or terminate the account of a User who has not completed a payment, or their access to other auctions or any section of the Platform."
                                        ]
                                    }
                                }
                            ]
                        },
                        "Delivery of the auctioned good:",
                        {
                            type: 'ul',
                            items: [
                                "Crezar will communicate to the Streamer the email address provided by the winning User of the auction. The Streamer will contact the User in order to arrange the details to proceed with the delivery of the goods.",
                                "The packaging and transport costs of the awarded goods will be borne by the Streamer. The successful bidder assumes the risk of loss or damage that may be caused to the goods as soon as they are delivered to the carrier. It is the responsibility of the Streamer to obtain the documentation, licenses and permits of any kind (including export and import) that may be required by the authorities for the transport of the awarded goods."
                            ]
                        },
                        "Fees:",
                        {
                            type: 'ul',
                            items: [
                                "Crezar charges an eight percent (8%) fee on all of the Viewer Payments received by the Streamer for the sale of auctioned goods.",
                                "In the event that the Streamer has become a Partner as provided in the Partner Terms and Conditions, Crezar will reduce this fee to four percent (4%).",
                                "This fee is deducted from the Viewer Payments."
                            ]
                        }
                    ]
                },
                {
                    title: "7.3. Subs",
                    content: [
                        "Streamers may offer a subscription service to Viewers which will be governed by this section and the other Terms of Service.",
                        "Viewers will be able to subscribe to Streamers who are enrolled in the 'Subs' mode.",
                        "Subs content:",
                        {
                            type: 'ul',
                            items: [
                                "The Subs service allows the Viewers who contract it to acquire the number of monthly stickers set by the Streamer, which must be in any case more than one (1) (“Sticker”)."
                            ]
                        },
                        "Functionality of Subs for the Streamer:",
                        {
                            type: 'ul',
                            items: [
                                "The Streamer offering Subs to Viewers must keep this functionality active in their profile. The Streamer may, at any time, terminate the 'Sub' functionality through their profile. In the description of the Sub, the Streamer may offer additional services or benefits to the Viewer included in the price of the monthly Sub."
                            ]
                        },
                        "Duration and Cancellation of Subs for the Viewer:",
                        {
                            type: 'ul',
                            items: [
                                "Subs have a monthly duration that is extended indefinitely for monthly periods. The billing will be performed on a monthly basis, counting from the date when the contracting took place.",
                                "You can cancel the Sub at any time, and you will continue to have access to the Sub until the end of the billing period (current month). Payments are non-refundable and we do not provide refunds or credits for partial subscription periods, Stickers, or any other unused content, even in the case where the streamer has stopped streaming and this has happened as a result.",
                                "Stickers or any other content that has not been used in a subscription period cannot be accumulated for the following period.",
                                "To cancel your Sub, access your page and cancel it. Such cancellation must be made at least three (3) calendar days in advance to the billing date. Otherwise, you authorize us to charge the subscription fee for the next billing cycle on your payment method."
                            ]
                        },
                        "Fees:",
                        {
                            type: 'ul',
                            items: [
                                "Crezar charges a twenty percent (20%) fee on each and every Viewer Payment derived from the contracting of Subs.",
                                "This fee is deducted from the Viewer Payments."
                            ]
                        }
                    ]
                }
            ]
        },
        {
            title: "8. SHOPPING PERFORMED BY VIEWERS",
            content: [
                "Acquisitions:",
                {
                    type: 'ul',
                    items: [
                        "All transactions and interactions facilitated on the Platform are contracts between the Viewers and the Streamers. Although we facilitate transactions and interactions between Viewers and Streamers by providing the Platform and storing content, Crezar is not a party to the contract between Viewers and Streamers and is not responsible for any transactions or interactions between Viewers and Streamers. Similarly, the fact that Crezar receives payments from Viewers on behalf of Streamers does not in any way imply that Crezar assumes any liability.",
                        "When a Viewer has made the required payment, the Streamer must allow the Viewer to access the corresponding Content and perform the interaction, if applicable."
                    ]
                },
                "Payment methods:",
                {
                    type: 'ul',
                    items: [
                        "All prices are in euros (EUR) and US dollars (USD), and Viewer Payments will be charged in the relevant currency. We have no control over currency exchange rates or charges imposed by the payment card provider or bank, and we are not responsible for any charges or fees imposed by the payment card provider or bank.",
                        "Viewer Payments include VAT where applicable.",
                        "The Viewer authorizes Crezar to provide their payment card details to a third-party payment provider in order to process the Viewer Payment to the Streamer.",
                        "The Viewer expressly agrees that they will not make any unwarranted refund requests from any Streamer or unwarranted chargeback requests from the payment card provider in connection with any transaction they make with a Streamer."
                    ]
                },
                "Withholdings:",
                {
                    type: 'ul',
                    items: [
                        "Crezar may fully or partially withhold the Viewer Payments in the following cases:",
                        {
                            type: 'ul',
                            items: [
                                "If the Viewer has breached or we believe that the Viewer has breached any of the Terms of Service.",
                                "If we suspect that any or all of the Viewer Payments are the result of illegal activity performed either by you or by the Streamer to whom any Viewer Payments are due."
                            ]
                        },
                        "The withholding will last for the time necessary to investigate the breach or alleged illegal activity. In the event that after our investigation we conclude that such breach or illegal activity has in fact occurred, the obligation to deliver the withheld Viewer Payments to the Streamer will be automatically terminated and Crezar will take the actions we deem necessary.",
                        "The withholding will not generate any liability for Crezar, and no interest shall accrue to the Streamer."
                    ]
                }
            ]
        },
        {
            title: "9. PAYMENTS TO STREAMERS",
            content: [
                "Receiving payments and payment gateway configuration:",
                {
                    type: 'ul',
                    items: [
                        "All Viewer Payments will be received through a payment provider designated by Crezar. Viewer Payments will be received by Crezar acting as a commercial agent for the Streamers. Crezar will deduct the applicable fee from each Viewer Payment received.",
                        "A payment gateway of Crezar' choice (i.e., Hyperwallet or similar) may be used. This service allows you to make payments on the relevant day automatically and much faster, allowing you to add your bank as a payment method.",
                        "The payment gateway requires a small setup the first time you receive a payment. If this is your first payment received through the Platform, you will receive an email the same day the payment is made to activate and link your payment platform account to your Crezar account. You will not have to do anything until you receive the activation email. However, in order to receive it, your PayPal email must be correctly configured.",
                        "Payment gateway fees will be borne by the Streamer. If they have been previously paid by Crezar, they will be charged to the Streamer in the corresponding invoice.",
                        "The Viewer expressly agrees that they will not make any unwarranted refund requests from any Streamer or unwarranted chargeback requests from the payment card provider in connection with any transaction they make with a Streamer."
                    ]
                },
                "Time of payment:",
                {
                    type: 'ul',
                    items: [
                        "The money will go to the payment gateway account you have set up in the 'Billing Information' tab of your sales page. Crezar will make two (2) monthly payments to Streamers. The first payment will be made between the 1st and 5th day of the month and the second between the 15th and 20th day of the month, all in relation to sales collected fifteen (15) days in advance. The Streamer will be able to modify the payment system and receive a single monthly payment by configuring it in 'Billing Information'."
                    ]
                }
            ]
        },
        {
            title: "14. INDEMNIFICATION",
            content: [
                "You agree to defend, indemnify, and hold harmless Crezar and its licensee and licensors, and their employees, contractors, agents, officers, and directors, from and against any and all claims, damages, obligations, losses, liabilities, costs or debt, and expenses (including but not limited to attorney's fees), resulting from or arising out of a) your use and access of the Platform, by you or any person using your account and password; b) a breach of these Terms, or c) Content posted on the Platform."
            ]
        },
        {
            title: "15. GOVERNING LAW AND JURISDICTION",
            content: [
                "These ToS shall be governed and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions.",
                "Our failure to enforce any right or provision of these ToS will not be considered a waiver of those rights. If any provision of these ToS is held to be invalid or unenforceable by a court, the remaining provisions of these ToS will remain in effect. These ToS constitute the entire agreement between us regarding our Platform, and supersede and replace any prior agreements we might have had between us regarding the Platform."
            ]
        },
        {
            title: "16. CONTACT",
            content: [
                "If you have any questions about these ToS, please contact us at privacy@Crezar.com."
            ]
        }
    ];

    return (
        <div className="bg-neutral-950 flex flex-col min-h-screen font-sans antialiased text-neutral-50">

            <main className="flex-1 pt-8">
                <section className="py-8 px-6 md:px-8 bg-gradient-to-br from-black via-neutral-950 to-neutral-900">
                    <div className="max-w-4xl mx-auto">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-yellow-400/20 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i className="fa-solid fa-file-contract text-yellow-400 text-2xl"></i>
                            </div>
                            <h1 className="text-4xl md:text-5xl font-extrabold mb-4 bg-gradient-to-r from-yellow-400 via-yellow-200 to-yellow-300 bg-clip-text text-transparent">
                                Terms of Service
                            </h1>
                            <p className="text-xl text-yellow-100">Please read these terms carefully</p>
                        </div>

                        <div className="bg-neutral-900/80 border-2 border-neutral-800 rounded-2xl p-8 shadow-lg">
                            {sections.map((section, index) => (
                                <div key={index} className="mb-8 last:mb-0">
                                    <h2 className="text-2xl font-bold mb-4 text-yellow-200 flex items-center gap-3">
                                        <i className="fa-solid fa-gavel text-yellow-400"></i>
                                        {section.title}
                                    </h2>
                                    <div className="space-y-4 text-gray-300">
                                        {renderContent(section.content)}
                                        {section.subsections && section.subsections.map((subsection, subIndex) => (
                                            <div key={subIndex} className="ml-4 pl-4 border-l-2 border-neutral-700">
                                                <h3 className="text-xl font-bold mb-2 text-yellow-300">{subsection.title}</h3>
                                                <div className="space-y-4 text-gray-300">
                                                    {renderContent(subsection.content)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                            <div className="border-t border-neutral-700 pt-6 mt-8">
                                <div className="flex items-center justify-center gap-2 text-sm text-neutral-400">
                                    <i className="fa-solid fa-calendar-alt"></i>
                                    <span>Last updated: Jan 31, 2025</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
};

export default TermsAndConditions;